import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { socket } from "@/client/libs/socket";
import { queryClient } from "@/client/libs/query-client";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CENTRE D'AIDE (console admin) — services client
 * ─────────────────────────────────────────────────────────────────────────────
 *  API : /admin/help/requests (liste, détail, réponse, statut, assignation).
 *  Temps réel : socket `help:update` → invalidation de la file (plusieurs
 *  admins simultanés restent synchronisés).
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type HelpMessage = {
  id: string;
  helpRequestId: string;
  authorRole: "user" | "admin";
  authorId: string | null;
  body: string;
  internalNote: boolean;
  createdAt: string;
};

export type HelpRequest = {
  id: string;
  code: string;
  userId: string;
  user: { id: string; name: string; email: string };
  category: "DOWNLOAD_EXPORT" | "PAYMENT" | "ACCOUNT_SECURITY" | "AI_FEATURE" | "OTHER";
  subject: string;
  message: string;
  context?: Record<string, unknown> | null;
  status: "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
  priority: string;
  assignedAdminId: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  messages: HelpMessage[];
};

export type InternalAdmin = { id: string; name: string; email: string };

export const HELP_KEYS = {
  all: ["help"] as const,
  requests: (status?: string, category?: string) => ["help", "requests", status, category] as const,
  detail: (id: string) => ["help", "request", id] as const,
  admins: ["help", "admins"] as const,
};

const fetchHelpRequests = async (params: { status?: string; category?: string }) => {
  const response = await axios.get<HelpRequest[]>("/admin/help/requests", { params });
  return response.data;
};

const fetchHelpRequest = async (id: string) => {
  const response = await axios.get<HelpRequest>(`/admin/help/requests/${id}`);
  return response.data;
};

const fetchInternalAdmins = async () => {
  const response = await axios.get<InternalAdmin[]>("/admin/help/admins");
  return response.data;
};

/** File d'attente temps réel : invalide tout le domaine help sur socket. */
export const useHelpRequests = (status?: string, category?: string) => {
  const query = useQuery({
    queryKey: HELP_KEYS.requests(status, category),
    queryFn: () => fetchHelpRequests({ status, category }),
  });

  const queryClientRef = useQueryClient();
  useEffect(() => {
    const invalidate = () => queryClientRef.invalidateQueries({ queryKey: HELP_KEYS.all });
    socket.on("help:update", invalidate);
    return () => {
      socket.off("help:update", invalidate);
    };
  }, [queryClientRef]);

  return {
    requests: query.data,
    loading: query.isPending,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useHelpRequest = (id: string) => {
  const query = useQuery({
    queryKey: HELP_KEYS.detail(id),
    queryFn: () => fetchHelpRequest(id),
    enabled: Boolean(id),
  });

  const queryClientRef = useQueryClient();
  useEffect(() => {
    const invalidate = () => queryClientRef.invalidateQueries({ queryKey: HELP_KEYS.detail(id) });
    socket.on("help:update", invalidate);
    return () => {
      socket.off("help:update", invalidate);
    };
  }, [queryClientRef, id]);

  return { request: query.data, loading: query.isPending, error: query.error };
};

export const useInternalAdmins = () => {
  const query = useQuery({
    queryKey: HELP_KEYS.admins,
    queryFn: fetchInternalAdmins,
    staleTime: 5 * 60 * 1000,
  });
  return { admins: query.data ?? [], loading: query.isPending };
};

/** Réponse admin : message (+ note interne) → notif forceEmail au client. */
export const useRespondHelp = () => {
  return useMutation({
    mutationFn: async ({
      id,
      body,
      internalNote,
    }: {
      id: string;
      body: string;
      internalNote?: boolean;
    }) => {
      const response = await axios.post(`/admin/help/requests/${id}/messages`, {
        body,
        internalNote,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.all });
    },
  });
};

export const useSetHelpStatus = () => {
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: HelpRequest["status"] }) => {
      const response = await axios.patch(`/admin/help/requests/${id}/status`, { status });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.all });
    },
  });
};

export const useAssignHelp = () => {
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId?: string | null }) => {
      const response = await axios.patch(`/admin/help/requests/${id}/assign`, {
        adminId: adminId ?? null,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.all });
    },
  });
};
